import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "utils/api";
import mellbizzLogo from "assets/img/auth/Mellbizz-logo.png";

export default function ForgotPassword() {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState({ show: false, message: "", type: "error" });
    const [isLoading, setIsLoading] = useState(false);
    const [resetSent, setResetSent] = useState(false);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    useEffect(() => {
        if (toast.show) {
            const timer = setTimeout(() => {
                setToast({ show: false, message: "", type: "error" });
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [toast.show]);

    const validate = () => {
        const errs = {};
        const trimmedEmail = email.trim();

        // Check if email is empty
        if (!trimmedEmail) {
            errs.email = "Email is required";
        } else if (!emailRegex.test(trimmedEmail)) {
            errs.email = "Please enter a valid email address";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleChange = (e) => {
        const { value } = e.target;
        setEmail(value);
        setErrors((prev) => ({ ...prev, email: undefined }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: email.trim() }),
            });
            const data = await res.json();
            if (!res.ok) {
                if (Array.isArray(data.errors) && data.errors.length) {
                    const fieldErrors = {};
                    data.errors.forEach((errObj) => {
                        if (errObj.path) {
                            fieldErrors[errObj.path] = errObj.msg || data.message;
                        }
                    });
                    setErrors(fieldErrors);
                }
                setToast({ show: true, message: data?.message || "Failed to send reset link", type: "error" });
                return;
            }
            setToast({ show: true, message: "If an account with this email exists, a password reset link has been sent!", type: "success" });
            setResetSent(true);
            setTimeout(() => {
                navigate("/auth/sign-in");
            }, 4000);
        } catch (err) {
            setToast({ show: true, message: "Network error. Please try again.", type: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex h-screen flex-col bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 overflow-hidden">
            {/* Toast Notification */}
            {toast.show && (
                <div
                    className={`fixed top-6 left-1/2 z-50 w-full max-w-md -translate-x-1/2 transform rounded-2xl p-4 text-center text-sm font-medium text-white shadow-2xl backdrop-blur-md transition-all duration-300 ${toast.type === "success"
                            ? "border border-green-400 bg-green-500/90"
                            : "border border-red-400 bg-red-500/90"
                        }`}
                >
                    {toast.message}
                </div>
            )}

            {/* Background Decorative Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gradient-to-br from-indigo-200 to-purple-200 blur-3xl opacity-20 dark:from-indigo-900/20 dark:to-purple-900/20"></div>
                <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-200 to-cyan-200 blur-3xl opacity-20 dark:from-blue-900/20 dark:to-cyan-900/20"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-gradient-to-br from-purple-200 to-indigo-200 blur-3xl opacity-10 dark:from-purple-900/10 dark:to-indigo-900/10"></div>
            </div>

            {/* Main Content */}
            <div className="relative flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden">
                {/* Container */}
                <div className="w-full max-w-md space-y-8">
                    {/* Logo Section */}
                    <div className="flex flex-col items-center space-y-4">
                        <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg">
                            <img
                                src={mellbizzLogo}
                                alt="Mellbizz"
                                className="h-10 w-auto object-contain"
                            />
                        </div>
                        <div className="text-center">
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                Mellbizz
                            </h1>
                            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                                Business Management Platform
                            </p>
                        </div>
                    </div>

                    {/* Form Card */}
                    <div className="relative rounded-3xl border border-gray-200/50 dark:border-slate-700/50 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl shadow-2xl p-8 sm:p-10 space-y-6">
                        {/* Welcome Text */}
                        <div className="text-center space-y-2">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Reset your password
                            </h2>
                            {!resetSent && (
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Enter your email to receive a password reset link via email
                                </p>
                            )}
                        </div>

                        {!resetSent ? (
                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Email Input */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                                        <span className="flex items-center gap-2">
                                            <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            Email
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={handleChange}
                                        disabled={isLoading}
                                        className={`w-full rounded-xl border-2 bg-white/50 dark:bg-slate-900/30 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${errors.email
                                                ? "border-red-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-red-500 dark:focus:ring-red-900/30"
                                                : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 focus:border-indigo-500 dark:focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/30"
                                            }`}
                                    />
                                    {errors.email && (
                                        <p className="text-sm font-medium text-red-500 flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            {errors.email}
                                        </p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="mt-6 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 py-3 px-4 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:from-indigo-700 hover:to-purple-700 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-md dark:from-indigo-500 dark:to-purple-500 dark:hover:from-indigo-600 dark:hover:to-purple-600 flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg
                                                className="animate-spin h-5 w-5"
                                                xmlns="http://www.w3.org/2000/svg"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            Sending reset link...
                                        </>
                                    ) : (
                                        <>
                                            <span>Send Link</span>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                            </svg>
                                        </>
                                    )}
                                </button>

                                {/* Back to Sign In */}
                                <div className="text-center pt-2">
                                    <button
                                        type="button"
                                        onClick={() => navigate("/auth/sign-in")}
                                        className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors flex items-center justify-center gap-1 mx-auto"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        Back to Sign In
                                    </button>
                                </div>
                            </form>
                        ) : (
                            /* Success Message */
                            <div className="text-center space-y-4 py-6">
                                <div className="flex justify-center">
                                    <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-4">
                                        <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                        Reset link sent!
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        Check your email for a message with instructions to reset your password.
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-500">
                                        Redirecting to sign in page...
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Footer - Integrated */}
            <div className="relative border-t border-gray-200/30 dark:border-slate-700/30 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm flex items-center justify-center py-3">
                <div className="text-center text-xs text-gray-600 dark:text-gray-400">
                    <p>&copy; 2026 Mellbizz. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
}
